import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { ExportModal } from '@components/ExportModal';
import { CampoFecha } from '@components/CampoFecha';
import { DetalleLegajoDrawer } from './Busqueda';
import {
  getVentasCatalogos,
  listVentas,
  type AgenteVenta,
  type CanalVenta,
  type EstadoVenta,
} from '@lib/services/ventas.service';
import type { EquipoVenta } from '@lib/catalogs/equipos-venta';

const ESTADO_COLOR: Record<EstadoVenta, string> = {
  vendido: '#1a8c3c',
  ya_ms2: '#0d9488',
  acepto_en_pago: '#2563eb',
  oferta_enviada: '#7c3aed',
  en_gestion: '#b7791f',
  interesado: '#0891b2',
  perdido: '#dc2626',
  // `no_opto` (no eligió MS2 en el wizard) es base fría, no venta caída: gris
  // como `sin_gestion`, no rojo como `perdido`.
  no_opto: '#64748b',
  sin_gestion: '#64748b',
};

// Los labels de canal vienen del catálogo del backend (`/ventas-comisiones/
// catalogos`), que es el dueño de la clasificación. No duplicar el mapa acá:
// se desincroniza apenas cambia un canal.

const PLAN_COLOR: Record<string, string> = { MS1: '#64748b', MS2: '#1a8c3c', otro: '#b7791f' };

const money = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR');
}

/**
 * Celda de la columna Agente. Distingue a la persona identificada del canal
 * genérico: cuando la gestión vino del dialer de SkyTel no sabemos qué agente
 * atendió, y mostrarlo en itálica gris evita que se lea como un nombre propio.
 */
function AgenteCelda({ agente }: { agente: AgenteVenta | null }) {
  if (!agente) return <span className="text-element-mid">—</span>;
  if (agente.tipo === 'persona') {
    return <span title={agente.email ?? agente.nombre}>{agente.nombre}</span>;
  }
  return (
    <span className="text-element-mid" style={{ fontStyle: 'italic' }}>
      {agente.nombre}
    </span>
  );
}

/**
 * Celda de Monto MS2. El monto cobrado por el micrositio se muestra tal cual;
 * el de una venta manual es una ESTIMACIÓN (precio de lista vigente para ese
 * grupo, porque la plata entró por fuera), y va con "~" para que nadie lo sume
 * como cobrado.
 */
function MontoCelda({
  monto,
  estimado,
  capitas,
}: {
  monto: number | null;
  estimado: boolean;
  capitas: number;
}) {
  const sufijo = (
    <span className="text-element-mid" style={{ fontSize: 12 }}>
      {' '}
      ({capitas} cápita{capitas === 1 ? '' : 's'})
    </span>
  );
  if (monto == null) {
    return (
      <>
        <span className="text-element-mid">—</span>
        {sufijo}
      </>
    );
  }
  if (!estimado) {
    return (
      <>
        {money.format(monto)}
        {sufijo}
      </>
    );
  }
  return (
    <>
      <span
        style={{ fontStyle: 'italic' }}
        title="Estimado: la venta se cerró por fuera del sitio, así que no hay cobro propio. Es el precio de lista vigente para este grupo familiar."
      >
        ~{money.format(monto)}
      </span>
      {sufijo}
    </>
  );
}

/** Chip toggle para los filtros multi-select. */
function Chip({
  activo,
  label,
  count,
  color,
  onClick,
}: {
  activo: boolean;
  label: string;
  count?: number;
  color?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${activo ? color ?? '#2563eb' : 'var(--color-border, #E2E8F0)'}`,
        background: activo ? (color ?? '#2563eb') : 'transparent',
        color: activo ? '#fff' : 'var(--color-element-high)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {label}
      {typeof count === 'number' && (
        <span style={{ fontSize: 11, opacity: 0.8, fontVariantNumeric: 'tabular-nums' }}>
          {count.toLocaleString('es-AR')}
        </span>
      )}
    </button>
  );
}

export function VentasComisionesPage() {
  const [detalleLegajoId, setDetalleLegajoId] = useState<string | null>(null);
  const [estados, setEstados] = useState<Set<EstadoVenta>>(new Set());
  const [canales, setCanales] = useState<Set<CanalVenta>>(new Set());
  const [equipos, setEquipos] = useState<Set<EquipoVenta>>(new Set());
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [page, setPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const pageSize = 25;

  const catalogos = useQuery({ queryKey: ['ventas-catalogos'], queryFn: getVentasCatalogos });

  const ventas = useQuery({
    queryKey: [
      'ventas',
      [...estados].sort((a, b) => a.localeCompare(b)),
      [...canales].sort((a, b) => a.localeCompare(b)),
      [...equipos].sort((a, b) => a.localeCompare(b)),
      qDebounced,
      fechaDesde,
      fechaHasta,
      page,
    ],
    queryFn: () =>
      listVentas({
        estados: [...estados],
        canales: [...canales],
        equipos: [...equipos],
        q: qDebounced,
        fechaDesde,
        fechaHasta,
        page,
        pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  // Mismos filtros que la lista, como params del export CSV (`ventas-comisiones`
  // spec): "lo que ves es lo que descargás".
  const exportParams: Record<string, string> = {
    ...(estados.size ? { estados: [...estados].join(',') } : {}),
    ...(canales.size ? { canales: [...canales].join(',') } : {}),
    ...(equipos.size ? { equipos: [...equipos].join(',') } : {}),
    ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
    ...(fechaDesde ? { fechaDesde } : {}),
    ...(fechaHasta ? { fechaHasta } : {}),
  };

  const toggleEstado = (k: EstadoVenta) => {
    setPage(1);
    setEstados((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const toggleCanal = (k: CanalVenta) => {
    setPage(1);
    setCanales((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const toggleEquipo = (k: EquipoVenta) => {
    setPage(1);
    setEquipos((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const limpiar = () => {
    setEstados(new Set());
    setCanales(new Set());
    setEquipos(new Set());
    setQ('');
    setQDebounced('');
    setFechaDesde('');
    setFechaHasta('');
    setPage(1);
  };
  const buscar = () => {
    setPage(1);
    setQDebounced(q);
  };

  const canalLabel = (c: CanalVenta) =>
    catalogos.data?.canales.find((x) => x.key === c)?.label ?? c;

  const data = ventas.data;
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const hayFiltros =
    estados.size > 0 ||
    canales.size > 0 ||
    equipos.size > 0 ||
    qDebounced.trim().length > 0 ||
    fechaDesde.length > 0 ||
    fechaHasta.length > 0;

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Ventas / Comisiones</h1>
        <p className="text-element-mid">
          Estado de venta MS2 de la base afiliada, con el canal de la oferta. Filtrá por uno o
          varios estados, canales y equipos; hacé clic en una fila para ver el detalle del legajo.
        </p>
      </header>

      {/* Filtros */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input
            className="input"
            style={{ maxWidth: 360 }}
            placeholder="Buscar por apellido, nombre o DNI…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') buscar();
            }}
          />
          <button className="btn btn-secondary btn-sm" onClick={buscar}>
            Buscar
          </button>
          {hayFiltros && (
            <button className="btn btn-ghost btn-sm" onClick={limpiar}>
              Limpiar filtros
            </button>
          )}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-element-mid)', marginBottom: 6 }}>
          Fecha venta
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <CampoFecha
            ariaLabel="Fecha venta desde"
            value={fechaDesde}
            onChange={(iso) => {
              setPage(1);
              setFechaDesde(iso);
            }}
          />
          <span className="text-element-mid">a</span>
          <CampoFecha
            ariaLabel="Fecha venta hasta"
            value={fechaHasta}
            onChange={(iso) => {
              setPage(1);
              setFechaHasta(iso);
            }}
          />
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-element-mid)', marginBottom: 6 }}>
          Estado
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {(catalogos.data?.estados ?? []).map((e) => (
            <Chip
              key={e.key}
              activo={estados.has(e.key)}
              label={e.label}
              count={data?.porEstado?.[e.key]}
              color={ESTADO_COLOR[e.key]}
              onClick={() => toggleEstado(e.key)}
            />
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-element-mid)', marginBottom: 6 }}>
          Canal
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {(catalogos.data?.canales ?? []).map((c) => (
            <Chip
              key={c.key}
              activo={canales.has(c.key)}
              label={c.label}
              count={data?.porCanal?.[c.key]}
              onClick={() => toggleCanal(c.key)}
            />
          ))}
        </div>

        {/* Equipo del agente atribuible (VEN-516). Se asigna por usuario en Usuarios. */}
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-element-mid)', marginBottom: 6 }}>
          Equipo
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(catalogos.data?.equipos ?? []).map((eq) => (
            <Chip
              key={eq.key}
              activo={equipos.has(eq.key)}
              label={eq.label}
              count={data?.porEquipo?.[eq.key]}
              onClick={() => toggleEquipo(eq.key)}
            />
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="rowBetween" style={{ alignItems: 'center', marginBottom: 8 }}>
          <h2 className="heading-5">Legajos</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="text-element-mid" style={{ fontSize: 13 }}>
              {total.toLocaleString('es-AR')} resultado{total === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={() => setExportOpen(true)}
            >
              <Download size={14} /> Descargar
            </button>
          </div>
        </div>
        {ventas.isLoading && <TableSkeleton rows={8} />}
        {ventas.isError && <p className="status status-error">Error al cargar la lista.</p>}
        {data && data.items.length === 0 && !ventas.isLoading && (
          <p className="text-element-mid">No hay legajos que coincidan con los filtros.</p>
        )}
        {data && data.items.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Titular</th>
                  <th>DNI</th>
                  <th>Legajo</th>
                  <th>Plan</th>
                  <th>Estado venta MS2</th>
                  <th>Canal</th>
                  <th>Agente</th>
                  <th>Última gestión</th>
                  <th>Fecha venta</th>
                  <th style={{ textAlign: 'right' }}>Monto MS2</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr
                    key={it.legajoId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setDetalleLegajoId(it.legajoId)}
                    title="Ver detalle del legajo"
                  >
                    <td>
                      <strong>
                        {it.titular.apellido} {it.titular.nombres}
                      </strong>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{it.titular.documento}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{it.numero}</td>
                    <td>
                      {it.planActual ? (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: PLAN_COLOR[it.planActual] ?? '#64748b',
                          }}
                        >
                          {it.planActual}
                        </span>
                      ) : (
                        <span className="text-element-mid">—</span>
                      )}
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#fff',
                          background: ESTADO_COLOR[it.estadoVentaMs2],
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {catalogos.data?.estados.find((e) => e.key === it.estadoVentaMs2)?.label ??
                          it.estadoVentaMs2}
                      </span>
                      {it.esVentaManual && (
                        <span
                          title="Venta cerrada por fuera del sitio, registrada a mano"
                          style={{
                            display: 'inline-block',
                            marginLeft: 6,
                            padding: '2px 6px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#92400e',
                            background: '#fef3c7',
                            border: '1px solid #fcd34d',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          manual
                        </span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {it.canal ? canalLabel(it.canal) : <span className="text-element-mid">—</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <AgenteCelda agente={it.agente} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtFecha(it.ultimaGestionAt)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtFecha(it.fechaVenta)}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <MontoCelda
                        monto={it.monto}
                        estimado={it.montoEstimado}
                        capitas={it.capitas}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {total > pageSize && (
          <div className="rowBetween" style={{ alignItems: 'center', marginTop: 12 }}>
            <span className="text-element-mid" style={{ fontSize: 13 }}>
              Página {page} de {totalPaginas}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-ghost btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={page >= totalPaginas}
                onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {exportOpen && (
        <ExportModal
          tipo="ventas-comisiones"
          params={exportParams}
          titulo="Ventas / Comisiones"
          onClose={() => setExportOpen(false)}
        />
      )}

      {detalleLegajoId && (
        <DetalleLegajoDrawer
          source={{ tipo: 'legajo', legajoId: detalleLegajoId }}
          onClose={() => setDetalleLegajoId(null)}
          onAbrirLegajo={(id) => setDetalleLegajoId(id)}
        />
      )}
    </section>
  );
}
